import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Label, Text, Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Title3, Subtitle2, Avatar, MessageBar, MessageBarBody, MessageBarTitle, Checkbox, Spinner, Badge, Divider, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, TableColumnDefinition, createTableColumn } from '@fluentui/react-components';
import { CalendarToday28Regular, Dismiss24Regular, ChevronRightRegular, Add16Regular, Edit16Regular, EditRegular, Delete16Regular, AlertOn16Regular, Important16Regular, ArrowExit20Regular, Flag16Regular, BranchFork16Regular, CalendarLtr16Regular, Stethoscope20Regular, Building16Regular, Person16Regular, Tag16Regular, Link16Regular, ArrowSwap16Regular } from '@fluentui/react-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// component functions
import * as ComponentFunctions from './ConsumptionDetails.Functions';

// styles
import './ConsumptionDetails.Styles.css';
import ConsumptionListComponent from '../ConsumptionList/ConsumptionList.Component';
import ContractConceptSelectorComponent from '../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import PractitionerSelectorComponent from '../../../commons/component/Inputs/PractitionerSelector/PractitionerSelectorComponent';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { Provider } from '../../../models/Entities/Provider/Provider';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';

// components
import ConsumptionDetailMenuComponent from './ConsumptionDetailsMenu/ConsumptionDetailMenu.Component';
import ConsumptionAmountComponent from '../ConsumptionAmount/ConsumptionAmount.component';
import { ConsumptionStatus } from '../../../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Functions';
import ProtocolConsumptionStatusSelectorComponent from '../../../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Component';
import MedicalServiceMultiSelectorComponent from '../../MedicalServices/MedicalServiceSelector/MedicalServiceMultiSelectorComponent';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import ProviderSelectorComponent from '../../../commons/component/Inputs/ProviderSelector/ProviderSelectorComponent';
import ConsumptionSettlementComponent from '../ConsumptionSettlement/Consumption.Settlement.Component';
import ChangeTypeOfConsumptionComponent from '../ChangeTypeOfConsumption/ChangeTypeOfConsumption.Component';
import VisitConceptFormComponent from '../VisitConceptForm/VisitConcept.Form.Component';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import ChildConsumptionListComponent from '../ChildConsumptionList/ChildConsumption.List.Component';
import BilledInConceptListComponent from '../BilledInConceptList/BilledInConcept.List.Component';
import ConceptFormComponent from '../../Contracts/Concept/ConceptForm/ConceptForm.Component';
import ConceptValueFormComponent from './ConceptValues/ConceptValue.Form.Component';
import ConceptValueDeleteComponent from './ConceptValues/ConceptValue.Delete.Component';
import { ProtocolConsumptionConceptValues } from '../../../models/Entities/Protocols/ProtocolConsumptionConceptValues';
import { ProtocolConsumptionConditionEnum } from '../../../models/Enums/ProtocolConsumptionConditionEnum';


// ─── DataGrid value table types & helpers ────────────────────────────────────

type CellTone = 'default' | 'muted' | 'negative' | 'strong';

/**
 * A single numeric cell: a primary formatted value plus an optional muted note
 * (percentage applied, source currency, …). Keeping the note separate from the
 * value is what allows the grid to render a real visual hierarchy instead of
 * cramming "(50%) $1.234,00" into one string.
 */
interface NumericValue {
    value?: string;
    note?: string;
    tone?: CellTone;
}

const EMPTY_CELL: NumericValue = {};

interface ValueRow {
    id: string;
    concept: React.ReactNode;
    rate: NumericValue;
    unitAmount: NumericValue;
    amount: NumericValue;
    originalAmount: NumericValue;
    grossValue: NumericValue;
    withholding: NumericValue;
    ownerNet: NumericValue;
    teamNet: NumericValue;
    totalNet: NumericValue;
    rowKind: 'main' | 'additional' | 'section' | 'total-row';
}

const resolveCurrencyCode = (currency: string | undefined): string => {
    const normalized = (currency ?? '').trim().toUpperCase();
    // Intl.NumberFormat currency must be a valid 3-letter ISO code.
    if (/^[A-Z]{3}$/.test(normalized)) {
        return normalized;
    }
    return 'ARS';
};

const fmtCurrencyCode = (currency: string | undefined): string => {
    const normalized = (currency ?? '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalized) ? normalized : 'ARS';
};

const fmtCurrency = (amount: number | undefined, currency: string | undefined): string => {
    const safeCurrency = resolveCurrencyCode(currency);

    try {
        return (amount ?? 0).toLocaleString('es-AR', {
            style: 'currency',
            currency: safeCurrency,
        });
    } catch {
        return (amount ?? 0).toLocaleString('es-AR', {
            style: 'currency',
            currency: 'ARS',
        });
    }
};

/**
 * Amount without the currency symbol. The grid states its currency once in the
 * caption, so repeating the symbol on every cell only adds noise and breaks the
 * decimal alignment of the numeric columns.
 */
const fmtAmount = (amount: number | undefined): string =>
    (amount ?? 0).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const fmtNumber = (
    value: number | undefined,
    minimumFractionDigits: number = 2,
    maximumFractionDigits: number = 2
): string => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return '–';
    }

    return value.toLocaleString('es-AR', {
        minimumFractionDigits,
        maximumFractionDigits,
    });
};

const fmtPercent = (value: number | undefined): string => `${fmtNumber(value, 0, 3)}%`;

const NumericCell = (props: { cell: NumericValue; row: ValueRow }) => {
    const { cell, row } = props;
    const isDataRow = row.rowKind === 'main' || row.rowKind === 'additional';

    if (!cell.value) {
        // Section and total rows stay blank; data rows show an explicit "no value" mark.
        return isDataRow ? <span className="vgrid-num-empty">—</span> : null;
    }

    const toneClass = cell.tone && cell.tone !== 'default' ? ` vgrid-num--${cell.tone}` : '';

    return (
        <div className={`vgrid-num${toneClass}`}>
            {cell.value && <span className="vgrid-num-value">{cell.value}</span>}
            {cell.note && <span className="vgrid-num-note">{cell.note}</span>}
        </div>
    );
};

const buildValueColumns = (
    t: (key: string) => string,
    showQuotationAndOriginalAmount: boolean
): TableColumnDefinition<ValueRow>[] => {
    const numericColumn = (
        columnId: string,
        header: string,
        pick: (item: ValueRow) => NumericValue
    ) =>
        createTableColumn<ValueRow>({
            columnId,
            renderHeaderCell: () => header,
            renderCell: (item) => <NumericCell cell={pick(item)} row={item} />,
        });

    const columns: TableColumnDefinition<ValueRow>[] = [
        createTableColumn<ValueRow>({
            columnId: 'concept',
            renderHeaderCell: () => t('consumptionDetails.valuesGrid.concept'),
            renderCell: (item) => <div className="vgrid-concept">{item.concept}</div>,
        }),
        numericColumn('unitAmount', t('consumptionDetails.valuesGrid.unitAmount'), (item) => item.unitAmount),
        numericColumn('amount', t('consumptionDetails.valuesGrid.amount'), (item) => item.amount),
        numericColumn('grossValue', t('consumptionDetails.valuesGrid.grossValue'), (item) => item.grossValue),
        numericColumn('withholding', t('consumptionDetails.valuesGrid.withholding'), (item) => item.withholding),
        numericColumn('ownerNet', t('consumptionDetails.valuesGrid.center'), (item) => item.ownerNet),
        numericColumn('teamNet', t('consumptionDetails.valuesGrid.medicalTeam'), (item) => item.teamNet),
        numericColumn('totalNet', t('consumptionDetails.valuesGrid.total'), (item) => item.totalNet),
    ];

    if (showQuotationAndOriginalAmount) {
        columns.splice(1, 0,
            numericColumn('rate', t('consumptionDetailsGrid.quotation'), (item) => item.rate),
            numericColumn('originalAmount', t('consumptionDetails.valuesGrid.originAmount'), (item) => item.originalAmount)
        );
    }

    return columns;
};

// ─────────────────────────────────────────────────────────────────────────────

const PanelInfo = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();

    const [changeConceptOptionActive, setChangeConceptOptionActive] = useState(false);
    const [changeProducerOptionActive, setChangeProducerOptionActive] = useState(false);
    const [changeProviderOptionActive, setChangeProviderOptionActive] = useState(false);
    const [changeStageOptionActive, setChangeStageOptionActive] = useState(false);
    const [changeMedicalServiceOptionActive, setChangeMedicalServiceOptionActive] = useState(false);
    const [ChangeAll, setChangeAll] = useState<boolean>(false);
    const [changeProcedureOptionActive, setChangeProcedureOptionActive] = useState(false);
    const [changeAdditionalOptionActive, setChangeAdditionalOptionActive] = useState(false);

    // Arranca colapsada: es la seccion de edicion, no la de lectura. Quien abre
    // el detalle viene a ver importes y estado; clasificar es una accion que se
    // busca a proposito.
    const [isClassificationOpen, setIsClassificationOpen] = useState(false);
    const [isSettlementOpen, setIsSettlementOpen] = useState(true);

    // Cache groupingId so the related-concepts section stays visible while a transient
    // Redux update is in-flight (e.g. visit-type change). The cache is cleared when the
    // consumption is fully loaded and does not carry a groupingId.
    const [cachedGroupingId, setCachedGroupingId] = useState<string | undefined>(undefined);
    const isGroupedConsumption = Boolean(props.Consumption?.groupingId);
    useEffect(() => {
        if (props.Consumption?.groupingId) {
            setCachedGroupingId(props.Consumption.groupingId);
        } else if (!props.isLoading && props.Consumption?.id) {
            // Consumption fully loaded and has no groupingId: clear the cache so the
            // "Conceptos relacionados" section does not appear for unrelated consumptions.
            setCachedGroupingId(undefined);
        }
    }, [props.Consumption?.groupingId, props.Consumption?.id, props.isLoading]);

    const [newConceptAssociation, setNewConceptAssociation] = useState<ContractConcept>();
    const [newProducer, setNewProducer] = useState<Practitioner>();
    const [newProvider, setNewProvider] = useState<Provider>();
    const [newMedicalService, setNewMedicalService] = useState<AvailableMedicalService>();
    const [newStage, setNewStage] = useState<ContractStage>();
    const [showEditConceptForm, setShowEditConceptForm] = useState(false);

    // ABM de conceptos adicionales. `editingConceptValue` en undefined con el
    // formulario abierto significa alta; con valor, edicion de esa fila.
    const [showConceptValueForm, setShowConceptValueForm] = useState(false);
    const [editingConceptValue, setEditingConceptValue] = useState<ProtocolConsumptionConceptValues | undefined>(undefined);
    const [deletingConceptValue, setDeletingConceptValue] = useState<ProtocolConsumptionConceptValues | undefined>(undefined);

    useEffect(() => {
        if (props.Consumption?.protocolId) {
            props.GetAllProtocolContractAssociations(props.Consumption?.protocolId);
        }
    }, [props.Consumption?.protocolId]);

    useEffect(() => {
        if (props.Consumption?.contractConceptId) {
            props.GetContractConceptById(props.Consumption.contractConceptId);
        }
    }, [props.Consumption?.contractConceptId]);

    // Contract.addendums must be rehydrated first: GetAllContractsConceptByAddendum's
    // reducer silently no-ops if the addendum isn't already present in Contract.addendums,
    // and GetContractAddendumsByContractId itself no-ops unless Contract is already loaded.
    useEffect(() => {
        if (props.ContractId) {
            props.GetContractById(props.ContractId);
        }
    }, [props.ContractId]);

    useEffect(() => {
        if (props.ContractId) {
            props.GetContractAddendumsByContractId(props.ContractId);
        }
    }, [props.ContractId, props.ContractLoaded]);

    // Rehydrate the addendum's concept list so ConceptFormComponent's own selector
    // (which reads Contract.addendums[x].concepts, not the single ContractConcept) can find it.
    // Re-fires once AddendumLoaded flips true after the addendums rehydration above resolves.
    useEffect(() => {
        if (props.ContractId && props.ContractAddendumId !== undefined && props.AddendumLoaded) {
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
    }, [props.ContractId, props.ContractAddendumId, props.AddendumLoaded]);

    useEffect(() => {
        setChangeConceptOptionActive(props.isChangingConcept);
        setChangeAll(false);
    }, [props.isChangingConcept]);

    useEffect(() => {
        setChangeProducerOptionActive(props.isChangingProducer);
    }, [props.isChangingProducer]);

    useEffect(() => {
        setChangeProviderOptionActive(props.isChangingProvider);
    }, [props.isChangingProvider]);

    useEffect(() => {
        setChangeMedicalServiceOptionActive(props.isChangingMedicalService);
    }, [props.isChangingMedicalService]);

    // Close stage change panel when update succeeded
    useEffect(() => {
        if (changeStageOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeStageOptionActive(false);
            setNewStage(undefined);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    // Close procedure change panel when update succeeded
    useEffect(() => {
        if (changeProcedureOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeProcedureOptionActive(false);
            setChangeAll(false);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    // Close additional change panel when update succeeded
    useEffect(() => {
        if (changeAdditionalOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeAdditionalOptionActive(false);
            setChangeAll(false);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    const HandleChangeConcept = () => {
        if (props.ProtocolId && props.Consumption?.id && newConceptAssociation?.id) {
            props.ChangeContractConcept(props.ProtocolId, props.Consumption?.id, newConceptAssociation.id, ChangeAll);
        }
    };

    const HandleChangeProducer = () => {
        if (props.ProtocolId && props.Consumption?.id && newProducer?.userId) {
            props.ChangeProducer(props.ProtocolId, props.Consumption?.id, newProducer.userId);
        }
    };

    const HandleChangeProvider = () => {
        if (props.ProtocolId && props.Consumption?.id && newProvider) {
            newProvider && props.ChangeProvider(props.ProtocolId, props.Consumption?.id, newProvider);
        }
    };

    const HandleChangeMedicalService = () => {
        if (props.ProtocolId && props.Consumption?.id && newMedicalService?.id) {
            props.ChangeMedicalService(props.ProtocolId, props.Consumption?.id, newMedicalService?.id);
        }
    };

    const HandleChangeStage = () => {
        if (props.Consumption && newStage?.id) {
            props.ChangeVisit(props.Consumption.protocolId, props.Consumption.id, newStage.id);
        }
    };

    // Procedimiento y adicional comparten la bandera `isUpdating` del consumo, así
    // que no alcanza para saber cuál se tocó: sin esto girarían las dos a la vez.
    const [pendingMark, setPendingMark] = useState<'procedure' | 'additional' | null>(null);
    const wasUpdatingRef = React.useRef(false);

    useEffect(() => {
        const updating = !!props.Consumption?.isUpdating;

        if (wasUpdatingRef.current && !updating) {
            setPendingMark(null);
        }

        wasUpdatingRef.current = updating;
    }, [props.Consumption?.isUpdating]);

    const isMarkBusy = (mark: 'procedure' | 'additional'): boolean =>
        pendingMark === mark && !!props.Consumption?.isUpdating;

    const HandleSetProcedure = (isProcedure: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            setPendingMark('procedure');
            props.ChangeProcedure(props.ProtocolId, props.Consumption.id, isProcedure, ChangeAll);
        }
    };

    // Ninguna de las cinco marcas tiene una bandera propia: se leen de donde el
    // backend las deja. Procedimiento y adicional se derivan igual que en la
    // grilla de consumos (tag4 y typeOfService), la condición llega en
    // `condition` y la agrupación en `parentGroupingId`.
    const isProcedure = (props.Consumption?.tag4?.length ?? 0) > 0;
    const isAdditional = props.Consumption?.typeOfService === 2;
    const isEarlyDiscontinuation = props.Consumption?.condition === ProtocolConsumptionConditionEnum.EarlyDiscontinuation;
    const isFollowUp = props.Consumption?.condition === ProtocolConsumptionConditionEnum.FollowUp
        || !!props.Consumption?.markedAsFollowUp;
    const isGroupedInVisit = !!props.Consumption?.parentGroupingId;

    const HandleSetEarlyDiscontinuation = (isEarly: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            props.SetConsumptionEarlyDiscontinuation(props.ProtocolId, props.Consumption.id, isEarly);
        }
    };

    const HandleMarkFollowUp = () => {
        if (props.ProtocolId && props.Consumption?.id) {
            props.MarkConsumptionFollowUp(props.ProtocolId, props.Consumption.id);
        }
    };

    const HandleSetGrouping = (group: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            props.SetConsumptionGrouping(props.ProtocolId, props.Consumption.id, group);
        }
    };

    const HandleSetAdditional = (isAdditional: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            setPendingMark('additional');
            props.ChangeAdditional(props.ProtocolId, props.Consumption.id, isAdditional, ChangeAll);
        }
    };

    const getStatusColor = (status: number): 'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning' => {
        switch (status) {
            case 2: return 'success';
            case 3: return 'success';
            case 4: return 'danger';
            case 5: return 'subtle';
            default: return 'warning';
        }
    };

    const showQuotationAndOriginalAmount = useMemo(() => {
        const currentCurrencyLabel = fmtCurrencyCode(props.Consumption?.currencyCode);
        const originalCurrencyLabel = fmtCurrencyCode(props.Consumption?.originalCurrencyCode);
        return !!props.Consumption?.originalCurrencyCode && currentCurrencyLabel !== originalCurrencyLabel;
    }, [props.Consumption?.currencyCode, props.Consumption?.originalCurrencyCode]);

    const valueColumns = useMemo(
        () => buildValueColumns(t, showQuotationAndOriginalAmount),
        [t, showQuotationAndOriginalAmount]
    );

    const settlementTotals = useMemo(() => {
        const c = props.Consumption;
        const ownerNet =
            (c?.ownerAmountWithholdingTotal ?? 0) +
            (c?.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? cv.finalOwnerAmount : cv.ownerAmount), 0) ?? 0);
        const teamNet =
            (c?.teamAmountWithholdingTotal ?? 0) +
            (c?.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? cv.finalTeamAmount : cv.teamAmount), 0) ?? 0);
        return { ownerNet, teamNet, total: ownerNet + teamNet };
    }, [props.Consumption]);

    // Sin contrato no hay lista de conceptos de la cual elegir, asi que el ABM
    // de adicionales no se ofrece: seria un formulario que no puede completarse.
    const canEditConceptValues = Boolean(props.ContractId && props.Consumption?.id);

    // Falso positivo de react-hooks/rules-of-hooks. PanelInfo tiene un solo
    // `return` a nivel de funcion —el JSX final— y los 28 hooks estan todos
    // antes: ninguno es condicional. El aviso aparecio al crecer el JSX de la
    // seccion de clasificacion, lo que apunta al analisis de caminos de ESLint
    // rindiendose en una funcion de ~980 lineas. La solucion de fondo es partir
    // PanelInfo en componentes mas chicos; mientras tanto se silencia esta linea
    // sola, para que el resto de los hooks siga bajo control de la regla.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const valueRows = useMemo((): ValueRow[] => {
        const c = props.Consumption;
        if (!c) return [];

        const originalCurrencyLabel = fmtCurrencyCode(c.originalCurrencyCode);

        const hasQuantity = c.quantity !== undefined && c.quantity !== null && !Number.isNaN(c.quantity);
        const hasCoefficient = c.coefficientApplied !== undefined && c.coefficientApplied !== null && !Number.isNaN(c.coefficientApplied);
        // Cantidad y coeficiente viajan en la misma nota secundaria que usa el resto
        // de la grilla: una sola linea atenuada bajo el importe, sin alterar el alto de fila.
        const amountNote = [
            hasQuantity ? `Cant. ${fmtNumber(c.quantity, 0, 3)}` : undefined,
            hasCoefficient ? `Coef. ${fmtNumber(c.coefficientApplied, 2, 3)}` : undefined,
        ].filter(Boolean).join(" · ") || undefined;

        // ── Main concept row ──────────────────────────────────────────
        const mainRow: ValueRow = {
            id: 'main',
            concept: (
                <div className="vgrid-conceptCell">
                    <div className="vgrid-conceptLabel">
                        <span>{c.conceptDescription || t('consumptionDetails.valuesGrid.mainConcept')}</span>
                    </div>
                    {/* El overhead se muestra como nota, no como callout: un popover dentro de una
                        celda sticky queda atrapado en su contexto de apilamiento y recortado por el
                        contenedor con overflow de la grilla. */}
                    {c.haveOverheadIncluded && (
                        <span className="vgrid-conceptNote">
                            Overhead ({fmtNumber(c.overheadPercentage, 0, 3)}%) {fmtAmount(c.overheadAmount)}
                        </span>
                    )}
                    {!c.groupingId && (
                        <div className="vgrid-conceptEditor">
                            <ConsumptionAmountComponent
                                ProtocolId={props.ProtocolId}
                                ProtocolConsumptionId={c.id}
                                showCurrentValue={false}
                            />
                        </div>
                    )}
                </div>
            ),
            rate: showQuotationAndOriginalAmount
                ? { value: fmtNumber(c.rate, 2, 3), tone: 'muted' }
                : EMPTY_CELL,
            originalAmount: showQuotationAndOriginalAmount && c.originalAmount !== undefined
                ? { value: fmtAmount(c.originalAmount), note: originalCurrencyLabel, tone: 'muted' }
                : EMPTY_CELL,
            unitAmount: { value: fmtAmount(c.unitAmount) },
            amount: { value: fmtAmount(c.amount), note: amountNote },
            grossValue: { value: fmtAmount(c.amount) },
            withholding: c.withHolingAmount
                ? {
                    value: `−${fmtAmount(c.withHolingAmount)}`,
                    note: c.withHolingPercentage ? fmtPercent(c.withHolingPercentage) : undefined,
                    tone: 'negative',
                }
                : EMPTY_CELL,
            ownerNet: {
                value: fmtAmount(c.ownerAmountWithholdingTotal),
                note: fmtPercent(c.feeDivisionPercentage === 0 ? 100 : c.feeDivisionPercentage),
            },
            teamNet: {
                value: fmtAmount(c.teamAmountWithholdingTotal),
                note: fmtPercent(c.teamDivisionPercentage),
            },
            totalNet: {
                value: fmtAmount((c.ownerAmountWithholdingTotal ?? 0) + (c.teamAmountWithholdingTotal ?? 0)),
                tone: 'strong',
            },
            rowKind: 'main',
        };

        // ── Additional concept value rows ──────────────────────────────
        // La cabecera de la seccion es tambien donde se agrega: el alta vive
        // junto a las filas que crea, no en un menu aparte.
        const additionalSectionHeader = (
            <div className="vgrid-sectionCell">
                <span>{t('consumptionDetails.extraConcepts')}</span>
                {canEditConceptValues && (
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<Add16Regular />}
                        onClick={() => { setEditingConceptValue(undefined); setShowConceptValueForm(true); }}
                    >
                        {t('consumptionDetails.add')}
                    </Button>
                )}
            </div>
        );

        // La seccion se muestra tambien vacia cuando se puede editar: si no, no
        // habria donde agregar el primer concepto adicional.
        const showAdditionalSection = (c.conceptValues?.length ?? 0) > 0 || canEditConceptValues;

        const additionalRows: ValueRow[] = showAdditionalSection ? [
            {
                id: 'section-additional',
                concept: additionalSectionHeader,
                rate: EMPTY_CELL,
                unitAmount: EMPTY_CELL,
                amount: EMPTY_CELL,
                originalAmount: EMPTY_CELL,
                grossValue: EMPTY_CELL,
                withholding: EMPTY_CELL,
                ownerNet: EMPTY_CELL,
                teamNet: EMPTY_CELL,
                totalNet: EMPTY_CELL,
                rowKind: 'section' as const,
            },
            ...(c.conceptValues ?? []).map((cv): ValueRow => {
                const ownerNet = cv.applyWithholding ? cv.finalOwnerAmount : cv.ownerAmount;
                const teamNet  = cv.applyWithholding ? cv.finalTeamAmount  : cv.teamAmount;
                return {
                    id: `additional-${cv.id}`,
                    concept: (
                        <div className="vgrid-conceptCell vgrid-conceptCell--editable">
                            <span>{cv.contractConceptName}</span>
                            {canEditConceptValues && (
                                <span className="vgrid-conceptActions">
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<Edit16Regular />}
                                        aria-label={`Editar ${cv.contractConceptName}`}
                                        title={t('consumptionDetails.editConcept')}
                                        disabled={cv.isDeleting}
                                        onClick={() => { setEditingConceptValue(cv); setShowConceptValueForm(true); }}
                                    />
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={cv.isDeleting ? <Spinner size="extra-tiny" /> : <Delete16Regular />}
                                        aria-label={`Quitar ${cv.contractConceptName}`}
                                        title={t('consumptionDetails.removeConcept')}
                                        disabled={cv.isDeleting}
                                        onClick={() => setDeletingConceptValue(cv)}
                                    />
                                </span>
                            )}
                        </div>
                    ),
                    rate: EMPTY_CELL,
                    unitAmount: EMPTY_CELL,
                    amount: EMPTY_CELL,
                    originalAmount: EMPTY_CELL,
                    grossValue: { value: fmtAmount(cv.value), note: fmtPercent(cv.percentValue) },
                    withholding: cv.applyWithholding
                        ? {
                            value: `−${fmtAmount(cv.withholdingAmount)}`,
                            note: fmtPercent(cv.withholdingPercentage),
                            tone: 'negative',
                        }
                        : EMPTY_CELL,
                    ownerNet: {
                        value: fmtAmount(ownerNet),
                        note: fmtPercent(cv.feeDivisionPercentage === 0 ? 100 : cv.feeDivisionPercentage),
                    },
                    teamNet: {
                        value: fmtAmount(teamNet),
                        note: fmtPercent(cv.teamDivisionPercentage),
                    },
                    totalNet: { value: fmtAmount(ownerNet + teamNet), tone: 'strong' },
                    rowKind: 'additional',
                };
            }),
        ] : [];

        const totalWithholding =
            (c.withHolingAmount ?? 0) +
            (c.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? (cv.withholdingAmount ?? 0) : 0), 0) ?? 0);

        // ── Totals row ──────────────────────────────────────────────────
        const totalRow: ValueRow = {
            id: 'total-row',
            concept: t('consumptionDetails.valuesGrid.totalRow'),
            rate: EMPTY_CELL,
            unitAmount: EMPTY_CELL,
            amount: EMPTY_CELL,
            originalAmount: EMPTY_CELL,
            grossValue: EMPTY_CELL,
            withholding: totalWithholding
                ? { value: `−${fmtAmount(totalWithholding)}`, tone: 'negative' }
                : EMPTY_CELL,
            ownerNet: { value: fmtAmount(settlementTotals.ownerNet) },
            teamNet: { value: fmtAmount(settlementTotals.teamNet) },
            totalNet: { value: fmtCurrency(settlementTotals.total, c.currencyCode), tone: 'strong' },
            rowKind: 'total-row',
        };

        return [mainRow, ...additionalRows, totalRow];
    }, [props.Consumption, props.ProtocolId, showQuotationAndOriginalAmount, settlementTotals, canEditConceptValues]);

    const mainWithholdingAmount = props.Consumption?.withHolingAmount ?? 0;
    const additionalWithholdingAmount = props.Consumption?.conceptValues?.reduce(
        (acc, cv) => acc + (cv.applyWithholding ? (cv.withholdingAmount ?? 0) : 0),
        0
    ) ?? 0;
    const totalWithholdingAmount = mainWithholdingAmount + additionalWithholdingAmount;
    const showWithholdingMessage = totalWithholdingAmount > 0 || (props.Consumption?.withHolingPercentage ?? 0) > 0;

    // El bloque del profesional se usa en dos lugares: dentro del panel
    // colapsable y, para los consumos agrupados que no tienen ese panel,
    // como seccion propia. Se define una sola vez para no duplicarlo.
    const producerBlock = (
        <div className='consumptionPerson'>
            {props.failChangingProducer && <MessageBar key={props.Consumption?.id} intent={"error"}>
                <MessageBarBody>
                    <MessageBarTitle>{t('common.error')}</MessageBarTitle>
                    {props.errorChangingProducer?.ErrorMessage || props.errorChangingProducer?.Errors?.join(', ')}
                </MessageBarBody>
            </MessageBar>}
            {props.Consumption?.producer && <div className="personCard">
                <Avatar
                    aria-label={props.Consumption?.producer}
                    name={props.GetPersonByIdentifier(props.Consumption?.producer)?.fullName}
                    color="colorful"
                    size={40}
                />
                <div>
                    <Text>{props.GetPersonByIdentifier(props.Consumption?.producer)?.fullName}</Text>
                    <Label>{props.GetPersonByIdentifier(props.Consumption?.producer)?.speciality}</Label>
                </div>
            </div>}
            {!props.Consumption?.producer && <Text className="infoValueEmpty">{t('consumptionDetails.noProducer')}</Text>}
            {!isGroupedConsumption && <div className="buttonGroup">
                <Button appearance="outline" onClick={() => setChangeProducerOptionActive(true)}>{props.Consumption?.producer ? t('consumptionDetails.changeProducer') : t('consumptionDetails.reportProducer')}</Button>
            </div>}
        </div>
    );

    // Solo hay algo para plegar si el consumo tiene division de honorarios;
    // sin desglose el encabezado no se vuelve un control que no hace nada.
    const hasSettlementBreakdown = Boolean(props.Consumption?.haveFeeDivision);

    // El encabezado del total se usa como boton cuando hay desglose y como
    // bloque plano cuando no lo hay: el contenido es el mismo en ambos casos.
    const settlementHeadline = (
        <>
            <span className="consumptionSummary-headlineLabel">
                {hasSettlementBreakdown && (
                    <span className="section-toggle-chevron" aria-hidden="true">
                        <ChevronRightRegular />
                    </span>
                )}
                <span className="consumptionSummary-eyebrow">{t('consumptionDetails.totalToSettle')}</span>
                <span className="consumptionSummary-currency">{fmtCurrencyCode(props.Consumption?.currencyCode)}</span>
                {hasSettlementBreakdown && (
                    <span className="section-toggle-action">
                        {isSettlementOpen ? t('consumptionDetails.hideDetail') : t('consumptionDetails.showDetail')}
                    </span>
                )}
            </span>
            <span className="consumptionSummary-total">
                {fmtCurrency(settlementTotals.total, props.Consumption?.currencyCode)}
            </span>
        </>
    );

    return (
        <div className="consumptionPanel">
            {props.ProtocolConsumptionId && <ConsumptionDetailMenuComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={props.ProtocolConsumptionId} />}
            <div className='consumptionDetails'>
                <div className="infoField">
                    <Label>{t('consumptionDetails.patient')}</Label>
                    <Text>{props.Consumption?.patientNumberInProtocol}</Text>
                </div>
                <div className="infoField">
                    <Label>{t('consumptionDetails.dueDate')}</Label>
                    <Text>{moment(props.Consumption?.serviceDueDate).format('DD/MM/YYYY hh:mm a')}</Text>
                </div>
                <div className="infoField">
                    <Label>{t('consumptionDetails.status')}</Label>
                    <Badge color={getStatusColor(props.Consumption!.status)} appearance="tint" size="medium">
                        {ComponentFunctions.GetStatusDescription(props.Consumption!.status)}
                    </Badge>
                </div>
                {props.Consumption?.settlementId && <div className="infoField" style={{ gridColumn: '1 / -1' }}><ConsumptionSettlementComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={props.Consumption.id} /></div>}
            </div>

            {!isGroupedConsumption && <>
                <Divider className="section-divider" />
                {/* El encabezado es el control: un boton real, para que el foco y
                    Enter/Espacio funcionen sin manejar teclado a mano. */}
                <button
                    type="button"
                    className="section-toggle"
                    aria-expanded={isClassificationOpen}
                    aria-controls="classificationSection"
                    onClick={() => setIsClassificationOpen((open) => !open)}
                >
                    <span className="section-toggle-chevron" aria-hidden="true">
                        <ChevronRightRegular />
                    </span>
                    <Subtitle2 className="section-title">{t('consumptionDetails.classificationSection')}</Subtitle2>
                    {/* El estado va escrito y no solo en el giro del chevron: es lo
                        que despeja la duda de si el encabezado es un control. */}
                    <span className="section-toggle-action">
                        {isClassificationOpen ? t('consumptionDetails.hide') : t('consumptionDetails.show')}
                    </span>
                </button>
                <div id="classificationSection" className='RelatedConceptContainer section-panel' hidden={!isClassificationOpen}>
                    {props.ProtocolConsumptionId && (
                        <ChangeTypeOfConsumptionComponent ProtocolConsumptionId={props.ProtocolConsumptionId} ProtocolId={props.ProtocolId} />
                    )}

                    {/* Marcas del consumo, agrupadas por alcance. Antes las cinco
                        colgaban de una sola fila con un checkbox suelto abajo que
                        en realidad sólo afectaba a dos: quedaba imposible saber a
                        qué se aplicaba. Cada grupo dice ahora hasta dónde llega. */}
                    <p className="cls-sectionHint">
                        Las marcas se guardan apenas se tocan; no hay que confirmar.
                    </p>

                    <div className="cls-markGroup">
                        <span className="cls-markGroupTitle">{t('consumptionDetails.classification')}</span>
                        <div className="cls-marks" role="group" aria-label={t('consumptionDetails.classificationAria')}>
                            <button
                                type="button"
                                className={`cls-mark ${isProcedure ? 'is-on' : ''}`}
                                aria-pressed={isProcedure}
                                aria-busy={isMarkBusy('procedure')}
                                disabled={isMarkBusy('procedure')}
                                title={t('consumptionDetails.procedureTooltip')}
                                onClick={() => HandleSetProcedure(!isProcedure)}
                            >
                                {isMarkBusy('procedure') ? <Spinner size="extra-tiny" /> : <AlertOn16Regular />}
                                <span>{t('consumptionDetails.procedure')}</span>
                            </button>
                            <button
                                type="button"
                                className={`cls-mark ${isAdditional ? 'is-on' : ''}`}
                                aria-pressed={isAdditional}
                                aria-busy={isMarkBusy('additional')}
                                disabled={isMarkBusy('additional')}
                                title={t('consumptionDetails.additionalTooltip')}
                                onClick={() => HandleSetAdditional(!isAdditional)}
                            >
                                {isMarkBusy('additional') ? <Spinner size="extra-tiny" /> : <Important16Regular />}
                                <span>{t('consumptionDetails.additional')}</span>
                            </button>
                        </div>
                        {/* El alcance vive dentro del grupo que modifica: afuera no
                            se entendía a cuáles de las cinco marcas alcanzaba. */}
                        <Checkbox
                            className="cls-scope"
                            checked={ChangeAll}
                            onChange={(ev, data) => setChangeAll(!!data.checked)}
                            label={t('consumptionDetails.applyToSameType')}
                        />
                        <p className="cls-hint">
                            {ChangeAll
                                ? t('consumptionDetails.applyAllOn')
                                : t('consumptionDetails.applyAllOff')}
                        </p>
                    </div>

                    <div className="cls-markGroup">
                        <span className="cls-markGroupTitle">{t('consumptionDetails.conditionSection')}</span>
                        <div className="cls-marks" role="group" aria-label={t('consumptionDetails.conditionAria')}>
                            <button
                                type="button"
                                className={`cls-mark ${isEarlyDiscontinuation ? 'is-on' : ''}`}
                                aria-pressed={isEarlyDiscontinuation}
                                disabled={props.Consumption?.isChangingEarlyDiscontinuation}
                                title={t('consumptionDetails.earlyTerminationTooltip')}
                                onClick={() => HandleSetEarlyDiscontinuation(!isEarlyDiscontinuation)}
                            >
                                {props.Consumption?.isChangingEarlyDiscontinuation ? <Spinner size="extra-tiny" /> : <ArrowExit20Regular />}
                                <span>{t('consumptionDetails.earlyTermination')}</span>
                            </button>
                            <button
                                type="button"
                                className={`cls-mark ${isFollowUp ? 'is-on' : ''}`}
                                aria-pressed={isFollowUp}
                                disabled={isFollowUp || props.Consumption?.isMarkingFollowUp}
                                title={isFollowUp
                                    ? t('consumptionDetails.followUpDone')
                                    : t('consumptionDetails.followUpTooltip')}
                                onClick={HandleMarkFollowUp}
                            >
                                {props.Consumption?.isMarkingFollowUp ? <Spinner size="extra-tiny" /> : <Flag16Regular />}
                                <span>{t('consumptionDetails.followUp')}</span>
                            </button>
                            {!isGroupedConsumption && (
                                <button
                                    type="button"
                                    className={`cls-mark ${isGroupedInVisit ? 'is-on' : ''}`}
                                    aria-pressed={isGroupedInVisit}
                                    disabled={props.Consumption?.isChangingGrouping}
                                    title={t('consumptionDetails.groupedTooltip')}
                                    onClick={() => HandleSetGrouping(!isGroupedInVisit)}
                                >
                                    {props.Consumption?.isChangingGrouping ? <Spinner size="extra-tiny" /> : <BranchFork16Regular />}
                                    <span>{t('consumptionDetails.grouped')}</span>
                                </button>
                            )}
                        </div>
                        <p className="cls-hint">
                            Estas marcas afectan sólo a este consumo. La de seguimiento no se puede quitar una vez puesta.
                        </p>
                    </div>

                    {/* Procedimiento y adicional dejan su error en `failOnUpdating`,
                        que hasta ahora sólo se leía dentro del editor de visita: si
                        ese editor estaba cerrado, el rechazo no se veía en ningún
                        lado. Las cinco marcas reportan acá. */}
                    {(props.Consumption?.failOnEarlyDiscontinuationChange
                        || props.Consumption?.failOnGroupingChange
                        || props.Consumption?.FailMarkingFollowUp
                        || (props.Consumption?.failOnUpdating && !changeStageOptionActive)) && (
                        <MessageBar key={props.Consumption?.id} intent={'error'}>
                            <MessageBarBody>
                                <MessageBarTitle>{t('consumptionDetails.markError')}</MessageBarTitle>
                                {props.Consumption?.ErrorOnEarlyDiscontinuationChange?.ErrorMessage
                                    || props.Consumption?.ErrorOnGroupingChange?.ErrorMessage
                                    || (props.Consumption?.FailMarkingFollowUp
                                        ? (props.Consumption?.ErrorMarkingFollowUp?.ErrorCode === 409
                                            ? t('consumptionDetails.alreadyFollowUp')
                                            : props.Consumption?.ErrorMarkingFollowUp?.ErrorMessage)
                                        : props.Consumption?.error?.ErrorMessage)}
                            </MessageBarBody>
                        </MessageBar>
                    )}

                    {/* Asociaciones. Cada una es una tarjeta con su icono, su
                        etiqueta y su valor, y la grilla las acomoda en las columnas
                        que entren: sueltas sobre el mismo fondo la fila se leia
                        como un bloque continuo. El campo sin asociar va con borde
                        punteado y el que se esta editando toma la fila completa,
                        porque los selectores necesitan el ancho. */}
                    <div className="cls-grid">
                        {props.ContractId && (
                            <div className={`cls-field cls-field--assoc ${changeStageOptionActive ? 'is-editing' : ''} ${props.Consumption?.stageName ? '' : 'is-empty'}`}>
                                <span className="cls-fieldLabel">
                                    <span className="cls-fieldIcon" aria-hidden="true"><CalendarLtr16Regular /></span>
                                    {t('consumptionDetails.visit')}
                                </span>
                                {!changeStageOptionActive ? (
                                    <div className="cls-fieldBody">
                                        <Text className={props.Consumption?.stageName ? 'infoValue' : 'infoValueEmpty'}>
                                            {props.Consumption?.stageName || t('consumptionDetails.noVisit')}
                                        </Text>
                                        <Button appearance="subtle" size="small" icon={<EditRegular />} onClick={() => setChangeStageOptionActive(true)}>
                                            {props.Consumption?.stageName ? t('consumptionDetails.change') : t('consumptionDetails.associate')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="cls-editor">
                                        {props.Consumption?.failOnUpdating && (
                                            <MessageBar key={props.Consumption?.id} intent={'error'}>
                                                <MessageBarBody>
                                                    <MessageBarTitle>{t('common.error')}</MessageBarTitle>
                                                    {props.Consumption?.error?.ErrorMessage}
                                                </MessageBarBody>
                                            </MessageBar>
                                        )}
                                        <ContractStagesSelectorComponent
                                            ContractId={props.ContractId}
                                            onSelectedContractStage={(stages: ContractStage[]) => setNewStage(stages?.[0])}
                                            ContractStagesSelected={newStage ? [newStage.id] : props.Consumption?.stageId ? [props.Consumption?.stageId] : []}
                                            MultiselectEnable={false}
                                            noteText={undefined}
                                        />
                                        <div className="buttonGroup">
                                            <Button appearance="primary" disabled={props.Consumption?.isUpdating || newStage === undefined} onClick={HandleChangeStage}>
                                                {props.Consumption?.isUpdating ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                                            </Button>
                                            <Button appearance="subtle" disabled={props.Consumption?.isUpdating} onClick={() => setChangeStageOptionActive(false)}>{t('common.cancel')}</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={`cls-field cls-field--assoc ${changeMedicalServiceOptionActive ? 'is-editing' : ''} ${props.Consumption?.medicalServiceId ? '' : 'is-empty'}`}>
                            <span className="cls-fieldLabel">
                                <span className="cls-fieldIcon" aria-hidden="true"><Stethoscope20Regular /></span>
                                {t('consumptionDetails.medicalService')}
                            </span>
                            {!changeMedicalServiceOptionActive ? (
                                <div className="cls-fieldBody">
                                    <Text className={props.Consumption?.medicalServiceId ? 'infoValue' : 'infoValueEmpty'}>
                                        {props.Consumption?.medicalServiceId ? props.Consumption?.medicalServiceName : t('consumptionDetails.noMedicalService')}
                                    </Text>
                                    <Button appearance="subtle" size="small" icon={<EditRegular />} onClick={() => setChangeMedicalServiceOptionActive(true)}>
                                        {props.Consumption?.medicalServiceId ? t('consumptionDetails.change') : t('consumptionDetails.associate')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="cls-editor">
                                    {props.failChangingMedicalService && (
                                        <MessageBar key={props.Consumption?.id} intent={"error"}>
                                            <MessageBarBody>
                                                <MessageBarTitle>{t('common.error')}</MessageBarTitle>
                                                {props.errorChangingMedicalService?.ErrorMessage || props.errorChangingMedicalService?.Errors?.join(', ')}
                                            </MessageBarBody>
                                        </MessageBar>
                                    )}
                                    <MedicalServiceSelectorComponent
                                        onSelectedServices={(services: AvailableMedicalService[] | undefined) => services ? setNewMedicalService(services[0]) : setNewMedicalService(undefined)}
                                        MedicalServicesSelectedIds={newMedicalService ? [newMedicalService.id] : props.Consumption?.medicalServiceId ? [props.Consumption?.medicalServiceId] : []}
                                        MultiselectEnable={false} />
                                    <div className="buttonGroup">
                                        <Button appearance="primary" disabled={props.isChangingMedicalService || newMedicalService === undefined} onClick={HandleChangeMedicalService}>
                                            {props.isChangingMedicalService ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                                        </Button>
                                        <Button appearance="subtle" disabled={props.isChangingMedicalService} onClick={() => setChangeMedicalServiceOptionActive(false)}>{t('common.cancel')}</Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {!isGroupedConsumption && (
                            <div className={`cls-field cls-field--assoc ${changeProviderOptionActive ? 'is-editing' : ''} ${props.Consumption?.providerId ? '' : 'is-empty'}`}>
                                <span className="cls-fieldLabel">
                                    <span className="cls-fieldIcon" aria-hidden="true"><Building16Regular /></span>
                                    {t('consumptionDetails.provider')}
                                </span>
                                {!changeProviderOptionActive ? (
                                    <div className="cls-fieldBody">
                                        <Text className={props.Consumption?.providerId ? 'infoValue' : 'infoValueEmpty'}>
                                            {props.Consumption?.providerId ? props.Consumption?.providerName : t('consumptionDetails.noProvider')}
                                        </Text>
                                        <Button appearance="subtle" size="small" icon={<EditRegular />} onClick={() => setChangeProviderOptionActive(true)}>
                                            {props.Consumption?.providerId ? t('consumptionDetails.change') : t('consumptionDetails.associate')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="cls-editor">
                                        {props.failChangingProvider && (
                                            <MessageBar key={props.Consumption?.id} intent={"error"}>
                                                <MessageBarBody>
                                                    <MessageBarTitle>{props.errorChangingProvider?.ErrorMessage}</MessageBarTitle>
                                                    {t('consumptionDetails.verifyData')}
                                                </MessageBarBody>
                                            </MessageBar>
                                        )}
                                        <ProviderSelectorComponent
                                            onChangeSelection={(provider: Provider[] | undefined) => provider ? setNewProvider(provider[0]) : setNewProvider(undefined)}
                                            ProviderSelected={newProvider ? [newProvider.person.id] : props.Consumption?.providerId ? [props.Consumption?.providerId] as string[] : []}
                                            MultiselectEnable={false} />
                                        <div className="buttonGroup">
                                            <Button appearance="primary" disabled={props.isChangingProvider || newProvider === undefined} onClick={HandleChangeProvider}>
                                                {props.isChangingProvider ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                                            </Button>
                                            <Button appearance="subtle" disabled={props.isChangingProvider} onClick={() => setChangeProviderOptionActive(false)}>{t('common.cancel')}</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="cls-field cls-field--assoc cls-field--wide">
                            <span className="cls-fieldLabel">
                                <span className="cls-fieldIcon" aria-hidden="true"><Person16Regular /></span>
                                {t('consumptionDetails.producer')}
                            </span>
                            {producerBlock}
                        </div>
                    </div>
                </div>
            </>}

            {/* Los consumos agrupados no tienen panel de clasificacion; el
                profesional sigue visible como seccion propia, como hasta ahora. */}
            {isGroupedConsumption && <>
                <Divider className="section-divider" />
                <Subtitle2 className="section-title">{t('consumptionDetails.producer')}</Subtitle2>
                <div className='RelatedConceptContainer'>
                    {producerBlock}
                </div>
            </>}
            {!isGroupedConsumption && <>
                <Divider className="section-divider" />
                {/* El concepto pasa a hablar el mismo idioma visual que los campos del panel
                    de clasificacion (tarjeta con trazo lateral, etiqueta con icono y estados
                    vacio/edicion). Antes era un contenedor generico con dos botones "outline"
                    del mismo peso y el error incrustado dentro de la grilla del valor. */}
                <div className={[
                    'cls-field cls-field--assoc cls-field--wide',
                    !props.Consumption?.contractConceptId ? 'is-empty' : '',
                    changeConceptOptionActive ? 'is-editing' : ''
                ].filter(Boolean).join(' ')}>
                    <span className="cls-fieldLabel">
                        <span className="cls-fieldIcon" aria-hidden="true"><Tag16Regular /></span>
                        {t('consumptionDetails.conceptAssociated')}
                    </span>

                    {props.failChangingConcept && <MessageBar key={props.Consumption?.id} intent={"error"}>
                        <MessageBarBody>
                            <MessageBarTitle>{t('consumptionDetails.conceptAssociation')}</MessageBarTitle>
                            {props.errorChangingConcept?.ErrorMessage || props.errorChangingConcept?.Errors?.join(', ')}
                        </MessageBarBody>
                    </MessageBar>}

                    {!changeConceptOptionActive && <div className="cls-fieldBody">
                        <Text
                            className={props.Consumption?.conceptDescription ? 'infoValue' : 'infoValueEmpty'}
                            title={props.Consumption?.conceptDescription}
                        >
                            {props.Consumption?.conceptDescription ?? t('consumptionDetails.notAssociated')}
                        </Text>
                        <div className="buttonGroup">
                            {/* Con concepto ya asociado las dos acciones son secundarias; sin el,
                                asociar es la unica accion posible y toma el peso de primaria. */}
                            <Button
                                appearance={props.Consumption?.contractConceptId ? 'subtle' : 'primary'}
                                size="small"
                                icon={props.Consumption?.contractConceptId ? <ArrowSwap16Regular /> : <Link16Regular />}
                                onClick={() => setChangeConceptOptionActive(true)}
                            >
                                {props.Consumption?.contractConceptId ? t('consumptionDetails.change') : t('consumptionDetails.associateConcept')}
                            </Button>
                            {props.Consumption?.contractConceptId && props.ContractId && props.ContractAddendumId !== undefined && (
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<Edit16Regular />}
                                    onClick={() => setShowEditConceptForm(true)}
                                >
                                    {t('consumptionDetails.edit')}
                                </Button>
                            )}
                        </div>
                    </div>}

                    {changeConceptOptionActive && props.ContractId && <div className="cls-editor">
                        {/* ContractConceptsSelected se resuelve por id contra la lista de
                            conceptos: pasarle la descripcion no casa con nada y el tag
                            queda sin id ni detalle. */}
                        <ContractConceptSelectorComponent
                            ContractId={props.ContractId}
                            referralDate={props.Consumption?.serviceDueDate}
                            onSelectedContractConcept={(contract: ContractConcept[]) => setNewConceptAssociation(contract[0])}
                            ContractConceptsSelected={props.Consumption?.contractConceptId ? [props.Consumption.contractConceptId] : []}
                            MultiselectEnable={false}
                        />
                        <Checkbox
                            checked={ChangeAll}
                            onChange={(ev, data) => data.checked === true ? setChangeAll(data.checked) : setChangeAll(false)}
                            label={t('consumptionDetails.applyToAllOfType')}
                        />
                        <div className="buttonGroup">
                            <Button appearance="primary" size="small" disabled={props.isChangingConcept || newConceptAssociation === undefined} onClick={HandleChangeConcept}>
                                {props.isChangingConcept ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                            </Button>
                            <Button appearance="subtle" size="small" disabled={props.isChangingConcept} onClick={() => setChangeConceptOptionActive(false)}>{t('common.cancel')}</Button>
                        </div>
                    </div>}
                </div>
                {props.ContractId && props.ContractAddendumId !== undefined && props.Consumption?.contractConceptId && (
                    <ConceptFormComponent
                        isOpen={showEditConceptForm}
                        openPanel={() => setShowEditConceptForm(true)}
                        dismissPanel={() => setShowEditConceptForm(false)}
                        ContractId={props.ContractId}
                        ContractAddendumId={props.ContractAddendumId}
                        ContractConceptId={props.Consumption.contractConceptId}
                        CurrentConcepts={props.CurrentConcepts}
                        CurrentStages={props.CurrentStages}
                    />
                )}
            </>}
            <div className='consumptionSummary'>
                {/* La cifra ancla queda siempre visible: lo que se pliega es el
                    desglose. Ocultar el total seria esconder justo el dato que
                    se viene a buscar. */}
                {hasSettlementBreakdown ? (
                    <button
                        type="button"
                        className="consumptionSummary-headline consumptionSummary-headline--toggle"
                        aria-expanded={isSettlementOpen}
                        aria-controls="settlementBreakdown"
                        onClick={() => setIsSettlementOpen((open) => !open)}
                    >
                        {settlementHeadline}
                    </button>
                ) : (
                    <div className="consumptionSummary-headline">
                        {settlementHeadline}
                    </div>
                )}
                {hasSettlementBreakdown && (
                    <div id="settlementBreakdown" hidden={!isSettlementOpen}>
                        <div className="consumptionSummary-block">
                            <span>{t('consumptionDetails.divisionFeeNet')}</span>
                            <div className="consumptionSummary-row">
                                <span>{t('consumptionDetails.center')}</span>
                                <span className="consumptionSummary-value">{fmtAmount(settlementTotals.ownerNet)}</span>
                            </div>
                            <div className="consumptionSummary-row">
                                <span>{t('consumptionDetails.medicalTeam')}</span>
                                <span className="consumptionSummary-value">{fmtAmount(settlementTotals.teamNet)}</span>
                            </div>
                        </div>
                        {showWithholdingMessage && <div className="consumptionSummary-block" key={props.Consumption?.id}>
                            <span>{t('consumptionDetails.totalWithholding')}</span>
                            <div className="consumptionSummary-row">
                                <span>{t('consumptionDetails.main')}</span>
                                <span className="consumptionSummary-value consumptionSummary-value--negative">−{fmtAmount(mainWithholdingAmount)}</span>
                            </div>
                            {additionalWithholdingAmount > 0 && (
                                <div className="consumptionSummary-row">
                                    <span>{t('consumptionDetails.additionals')}</span>
                                    <span className="consumptionSummary-value consumptionSummary-value--negative">−{fmtAmount(additionalWithholdingAmount)}</span>
                                </div>
                            )}
                            <div className="consumptionSummary-row consumptionSummary-row--total">
                                <span>{t('consumptionDetails.totalWithheld')}</span>
                                <span className="consumptionSummary-value consumptionSummary-value--negative">−{fmtAmount(totalWithholdingAmount)}</span>
                            </div>
                        </div>}
                    </div>
                )}
            </div>
            <Divider className="section-divider" />
            <div className="valuesGrid">
                <div className="valuesGrid-caption">
                    <Subtitle2 className="section-title">{t('consumptionDetails.valuesDetail')}</Subtitle2>
                    <span className="valuesGrid-currency">
                        {t('consumptionDetails.amountsIn', { currency: fmtCurrencyCode(props.Consumption?.currencyCode) })}
                    </span>
                </div>
                <div className="valuesGrid-scroll">
                    <DataGrid
                        items={valueRows}
                        columns={valueColumns}
                        getRowId={(item) => item.id}
                        className="valuesDataGrid"
                        size="small"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {(column) => (
                                    <DataGridHeaderCell className={`vgrid-cell vgrid-cell--${column.columnId}`}>
                                        {column.renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<ValueRow>>
                            {({ item, rowId }) => (
                                <DataGridRow<ValueRow>
                                    key={rowId}
                                    className={`vgrid-row vgrid-row--${item.rowKind}`}
                                >
                                    {(column) => (
                                        <DataGridCell className={`vgrid-cell vgrid-cell--${column.columnId}`}>
                                            {column.renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            </div>

            {props.Consumption?.costAmount ? <>
                <Divider className="section-divider" />
                <Subtitle2 className="section-title">{t('consumptionDetails.costs')}</Subtitle2>
                <div className="consumptionCostCard">
                    <div className="consumptionCostCard-row">
                        <span className="consumptionCostCard-label">{t('consumptionDetails.calculatedCost')}</span>
                        <span className="consumptionCostCard-value">
                            {fmtCurrency(props.Consumption.costAmount, props.Consumption.costCurrencyCode ?? 'ARS')}
                        </span>
                    </div>
                    {props.Consumption.costPayee ? <div className="consumptionCostCard-row">
                        <span className="consumptionCostCard-label">{t('consumptionDetails.paid')}</span>
                        <span className="consumptionCostCard-value">
                            {fmtCurrency(props.Consumption.costPayee, props.Consumption.costCurrencyCode ?? 'ARS')}
                        </span>
                    </div> : null}
                    {props.Consumption.costPending ? <div className="consumptionCostCard-row">
                        <span className="consumptionCostCard-label">{t('consumptionDetails.pending')}</span>
                        <span className="consumptionCostCard-value consumptionCostCard-value--pending">
                            {fmtCurrency(props.Consumption.costPending, props.Consumption.costCurrencyCode ?? 'ARS')}
                        </span>
                    </div> : null}
                </div>
            </> : null}

            {(cachedGroupingId !== undefined && cachedGroupingId !== null) && <>
                <Divider className="section-divider" />
                <div className="relatedConcepts">
                    <div className="relatedConcepts-caption">
                        <Subtitle2 className="section-title">{t('consumptionDetails.relatedConcepts')}</Subtitle2>
                        <span className="relatedConcepts-hint">
                            Consumos agrupados junto a este concepto. Seleccione una fila para ver su detalle.
                        </span>
                    </div>
                    <ChildConsumptionListComponent ProtocolId={props.ProtocolId} CosnumptionId={props.Consumption?.id} />
                </div>
            </>}

            {/* Conceptos facturados dentro del concepto de contrato de este consumo.
                Se consultan al servidor al entrar al detalle. */}
            {props.Consumption?.id && <>
                <Divider className="section-divider" />
                <div className="billedInConcepts">
                    <div className="billedInConcepts-caption">
                        <Subtitle2 className="section-title">{t('consumptionDetails.billedInConcepts')}</Subtitle2>
                        <span className="billedInConcepts-hint">
                            Consumos incluidos dentro del concepto de contrato de este consumo. Seleccione una fila para ver su detalle.
                        </span>
                    </div>
                    <BilledInConceptListComponent ProtocolId={props.ProtocolId} CosnumptionId={props.Consumption?.id} />
                </div>
            </>}

            {/* ABM de conceptos adicionales. Los dos dialogos se montan al final
                del panel para que no queden anidados dentro de la grilla, que
                recorta su contenido. */}
            {canEditConceptValues && props.Consumption?.id && (
                <>
                    <ConceptValueFormComponent
                        ProtocolId={props.ProtocolId}
                        ProtocolConsumptionId={props.Consumption.id}
                        ContractId={props.ContractId}
                        ConceptValue={editingConceptValue}
                        isOpen={showConceptValueForm}
                        onClose={() => { setShowConceptValueForm(false); setEditingConceptValue(undefined); }}
                    />
                    <ConceptValueDeleteComponent
                        ProtocolId={props.ProtocolId}
                        ProtocolConsumptionId={props.Consumption.id}
                        ConceptValue={deletingConceptValue}
                        isOpen={deletingConceptValue !== undefined}
                        onClose={() => setDeletingConceptValue(undefined)}
                    />
                </>
            )}
        </div >
    )
};


const ConsumptionDetailsCompoment = (props: ComponentFunctions.Props) => {

    const [t] = useTranslation();

    const HandleCancelForm = () => {
        props.onClose();
    };

    const [showVisitConceptForm, setShowVisitConceptForm] = useState(false);

    useEffect(() => {
        if (props.isOpen && props.ProtocolId && props.ProtocolConsumptionId) {
            props.GetProtocolConsumptionById(props.ProtocolId, props.ProtocolConsumptionId);
        }
    }, [props.isOpen, props.ProtocolConsumptionId]);

    const Footer = (
        <DrawerFooter>
            <Button appearance="subtle" onClick={HandleCancelForm}>{t('common.close')}</Button>
        </DrawerFooter>
    );

    return (
        <div>
            <OverlayDrawer
                position={'end'}
                open={props.isOpen}
                size='large'
                style={{ width: 'min(92vw, 1200px)' }}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <>
                                {props.Consumption?.emrCrossVersionId !== undefined && Number(props.Consumption?.parentInstanceId) !== 0  && <Button appearance="primary" onClick={() => setShowVisitConceptForm(true)}>
                                    {t('consumptionDetails.viewVisit')}
                                </Button>}
                                <Button
                                    appearance="subtle"
                                    aria-label={t('common.close')}
                                    icon={<Dismiss24Regular />}
                                    onClick={() => HandleCancelForm()}
                                />
                            </>
                        }
                    >
                        <div className='consumptionHeader'>
                            <span className='consumptionHeader-icon' aria-hidden="true">
                                <CalendarToday28Regular />
                            </span>
                            <div className='consumptionHeader-text'>
                                {props.Consumption?.stageName && (
                                    <span
                                        className='consumptionHeader-eyebrow'
                                        title={props.Consumption.stageName}
                                    >
                                        {props.Consumption.stageName}
                                    </span>
                                )}
                                <span
                                    className='consumptionHeader-title'
                                    title={props.Consumption?.serviceName}
                                >
                                    {props.Consumption?.serviceName}
                                </span>
                            </div>
                        </div>
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className='ScrollShadow'>
                    {props.Consumption && <PanelInfo {...props} />}
                </DrawerBody>
                {Footer}
            </OverlayDrawer>
            {showVisitConceptForm === true && props.Consumption && props.Consumption?.patientNumberInProtocol && props.ContractId && <VisitConceptFormComponent
                ContractId={props.ContractId}
                isOpen={showVisitConceptForm}
                onClose={() => setShowVisitConceptForm(false)}
                protocolId={props.ProtocolId}
                visitId={Number(props.Consumption?.parentInstanceId)}
                patientId={props.Consumption?.patientNumberInProtocol}
            />}
        </div>
    );

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionDetailsCompoment as any);

